import { useMemo } from 'react'

import {
  formatClock,
  formatInt,
  formatMinutes,
  formatPct,
  NO_VALUE,
  pctOf,
} from '../../lib/format.ts'
import type { FleetPayload, SilenceState, SilentAgent } from '../../lib/sse/types.ts'
import type { Tone } from '../primitives/Metric.tsx'
import { Metric } from '../primitives/Metric.tsx'
import { Panel } from '../primitives/Panel.tsx'
import { ReferenceStamp } from '../primitives/ReferenceStamp.tsx'
import { StatusDot } from '../primitives/StatusDot.tsx'
import { TABLE, TD, TD_NUM, TH, TH_NUM, THEAD, TR } from '../primitives/table.ts'
import { Tooltip } from '../primitives/Tooltip.tsx'

/**
 * Os três estados, e o peso visual de cada um.
 *
 * `anomalous_silence` é achado de segurança: o agente parou dentro da janela em
 * que historicamente reporta, e num EDR isso pode significar que quem está na
 * máquina derrubou o agente. Os outros dois são manutenção. Dar aos três a mesma
 * cor destruiria a única informação que a lista carrega.
 */
const STATE_SPEC: Record<SilenceState, { tone: Tone; label: string; rank: number; note: string }> = {
  anomalous_silence: {
    tone: 'crit',
    label: 'silêncio anômalo',
    rank: 0,
    note: 'Parou de reportar dentro da janela ativa histórica.',
  },
  never_connected: {
    tone: 'warn',
    label: 'nunca conectou',
    rank: 1,
    note: 'Provisionado e nunca reportou.',
  },
  expected_offline: {
    tone: 'neutral',
    label: 'offline esperado',
    rank: 2,
    note: 'Silencioso fora da janela histórica, ou ainda sem baseline confiável.',
  },
}

const STATE_TEXT: Record<Tone, string> = {
  neutral: 'text-fg-muted',
  accent: 'text-accent',
  ok: 'text-ok',
  warn: 'text-warn',
  crit: 'text-crit',
}

interface FleetHealthPanelProps {
  fleet: FleetPayload | null
  lastFleetAt: number | null
  className?: string
}

/**
 * Fleet health — the panel this screen exists for.
 *
 * The three not-connected states are deliberately *not* rendered as one bar or
 * one row of equal tiles. `anomalous_silence` is a security signal (an agent that
 * stopped reporting inside its active window may have been killed by whoever is
 * on the box); the other two are housekeeping. Seven silent agents inside a
 * 323-strong "offline" total is exactly the number an operator would skim past,
 * so silence gets the hero figure and the other two get a quiet secondary row.
 *
 * For the same reason there is no stacked proportion bar of the whole fleet:
 * 7 out of 8.537 is a sub-pixel sliver, and drawing it would hide the finding
 * behind a visualisation.
 */
export function FleetHealthPanel({ fleet, lastFleetAt, className }: FleetHealthPanelProps) {
  const agents = fleet?.agents ?? null

  // O servidor já ordena por tempo de silêncio; reordenamos por ESTADO primeiro
  // porque um `expected_offline` de 12 h ficaria acima do `anomalous_silence` de
  // 45 min, que é justamente a linha que este painel existe para mostrar.
  const silent = useMemo(
    () =>
      [...(fleet?.silent_agents ?? [])].sort(
        (a, b) =>
          STATE_SPEC[a.state].rank - STATE_SPEC[b.state].rank ||
          b.silent_for_minutes - a.silent_for_minutes,
      ),
    [fleet],
  )

  const silenceCount = agents?.anomalous_silence ?? 0
  const hasSilence = silenceCount > 0

  // As contagens vêm de `agents`, que cobre a frota inteira — a lista trunca em
  // 50. Num incidente amplo a tela precisa dizer "312 em silêncio" mesmo
  // exibindo 50 linhas, senão o operador subestima o tamanho do problema.
  const totalSilent =
    agents === null
      ? 0
      : agents.anomalous_silence + agents.expected_offline + agents.never_connected
  const truncated = totalSilent > silent.length

  return (
    <Panel
      title="Frota de agentes"
      weight="hero"
      className={className}
      meta={<ReferenceStamp fleet={fleet} lastFleetAt={lastFleetAt} />}
    >
      <div className="flex h-full min-h-0 flex-col gap-3">
        {/* The finding, at the weight the finding deserves. */}
        <div
          className={`flex items-start gap-4 border-l-2 py-1 pl-3 ${
            hasSilence ? 'border-crit' : 'border-ok'
          }`}
          style={
            hasSilence
              ? { backgroundColor: 'color-mix(in oklab, var(--c-crit) 9%, transparent)' }
              : undefined
          }
        >
          <Metric
            label="silêncio anômalo"
            value={formatInt(silenceCount)}
            size="xl"
            tone={hasSilence ? 'crit' : 'ok'}
            labelBelow
          />
        </div>

        {/* Everything below is context for the number above, and reads that way. */}
        <div className="grid grid-cols-3 gap-3 border-b border-line-subtle pb-3">
          <Metric
            label="conectados"
            value={formatInt(agents?.connected)}
            size="lg"
            note={
              agents === null ? undefined : (
                <>
                  de {formatInt(agents.total_provisioned)} provisionados ·{' '}
                  <span className="tnum">
                    {formatPct(pctOf(agents.connected, agents.total_provisioned), 1)}
                  </span>
                </>
              )
            }
          />
          <div>
            <span className="flex items-center gap-1.5">
              <StatusDot tone="neutral" />
              <span className="text-micro uppercase tracking-label text-fg-muted">
                offline esperado
              </span>
            </span>
            <div className="mt-0.5 text-base text-fg-secondary">
              <span className="live-value font-mono">{formatInt(agents?.expected_offline)}</span>
            </div>
          </div>
          <div>
            <span className="flex items-center gap-1.5">
              <StatusDot tone="warn" />
              <span className="text-micro uppercase tracking-label text-fg-muted">
                nunca conectou
              </span>
            </span>
            <div className="mt-0.5 text-base text-fg-secondary">
              <span className="live-value font-mono">{formatInt(agents?.never_connected)}</span>
            </div>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="mb-1 flex items-baseline justify-between">
            <h3 className="text-micro font-medium uppercase tracking-label text-fg-secondary">
              Agentes em silêncio
            </h3>
            {silent.length > 0 && (
              <span className="text-micro text-fg-muted">
                {truncated ? (
                  <>
                    <span className="tnum">{formatInt(silent.length)}</span> de{' '}
                    <span className="tnum">{formatInt(totalSilent)}</span>
                  </>
                ) : (
                  <>
                    <span className="tnum">{formatInt(silent.length)}</span> listado(s)
                  </>
                )}
              </span>
            )}
          </div>

          {silent.length === 0 ? (
            <p className="text-sm text-fg-muted">
              Sem endpoints silenciosos na última leitura da frota.
            </p>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto">
              <table className={TABLE}>
                <caption className="sr-only">
                  Endpoints que pararam de reportar dentro da janela em que costumam estar ativos
                </caption>
                <thead className={THEAD}>
                  <tr>
                    <th scope="col" className={TH}>
                      Host
                    </th>
                    <th scope="col" className={TH}>
                      Estado
                    </th>
                    <th scope="col" className={TH}>
                      Tenant
                    </th>
                    <th scope="col" className={TH_NUM}>
                      Silencioso
                    </th>
                    <th scope="col" className={TH}>
                      Janela ativa
                    </th>
                    <th scope="col" className={TH_NUM}>
                      Visto às
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {silent.map((agent) => (
                    <SilentRow key={agent.endpoint_id} agent={agent} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Panel>
  )
}

/**
 * Uma linha da lista de silêncio.
 *
 * `typical_active_hours` nula não é dado faltando: é endpoint com menos de 7
 * dias de histórico. E isso tem consequência — sem baseline ele **nunca** é
 * classificado como silêncio anômalo e nunca alerta, porque alerta de silêncio
 * em máquina recém-instalada é falso positivo garantido. A célula diz "sem
 * baseline" em vez de travessão, que sugeriria informação perdida.
 *
 * A janela vem em UTC e pode virar a meia-noite ("22:00-04:00" é uma máquina de
 * turno da noite), então nada aqui a interpreta — é renderizada verbatim.
 */
function SilentRow({ agent }: { agent: SilentAgent }) {
  const spec = STATE_SPEC[agent.state]
  const anomalous = agent.state === 'anomalous_silence'

  return (
    <tr className={TR}>
      <th
        scope="row"
        className={`${TD} font-mono font-normal ${anomalous ? 'text-fg' : 'text-fg-secondary'}`}
      >
        {agent.hostname}
      </th>
      <td className={TD}>
        <Tooltip content={spec.note}>
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <StatusDot tone={spec.tone} />
            <span className={`text-micro uppercase tracking-label ${STATE_TEXT[spec.tone]}`}>
              {spec.label}
            </span>
          </span>
        </Tooltip>
      </td>
      <td className={`${TD} truncate`}>{agent.tenant_name}</td>
      <td className={`${TD_NUM} ${anomalous ? 'text-crit' : 'text-fg-secondary'}`}>
        {formatMinutes(agent.silent_for_minutes)}
      </td>
      <td className={`${TD} font-mono tnum`}>
        {agent.typical_active_hours ?? <span className="font-sans text-fg-muted">sem baseline</span>}
      </td>
      {/* Nulo só ocorre em `never_connected` — o host nunca reportou, e o
          travessão é a leitura correta: não há "visto às". */}
      <td className={TD_NUM}>
        {agent.last_seen === null ? NO_VALUE : formatClock(agent.last_seen)}
      </td>
    </tr>
  )
}
